import { Card, CardContent } from "../../../../ui/card";
import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import { Star, MapPin } from "lucide-react";
import { format } from "date-fns";

interface ReviewCardProps {
  review: {
    id: string;
    author_name: string | null;
    comment_text: string;
    rating: number | null;
    platform: string;
    posted_at: string | null;
    is_featured: boolean | null;
    outlets?: { outlet_name: string } | null;
  };
  onToggleFeatured: (id: string, currentStatus: boolean) => void;
  isUpdating: boolean;
}

export const ReviewCard = ({ review, onToggleFeatured, isUpdating }: ReviewCardProps) => {
  const renderStars = (rating: number | null) => {
    if (!rating) return null;
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < rating ? "fill-yellow-400 text-yellow-400" : "text-muted"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h4 className="font-semibold">
              {review.author_name && review.author_name.trim().toLowerCase() !== "anonymous"
                ? review.author_name
                : "Verified Customer"}
            </h4>
            <div className="flex items-center gap-2">
              {renderStars(review.rating)}
              <Badge variant="secondary" className="text-xs">
                {review.platform}
              </Badge>
            </div>
          </div>
          <Button
            size="sm"
            variant={review.is_featured ? "default" : "outline"}
            onClick={() => onToggleFeatured(review.id, review.is_featured || false)}
            disabled={isUpdating}
          >
            {review.is_featured ? "Featured ⭐" : "Add to Wall"}
          </Button>
        </div>

        {/* Review Text */}
        <p className="text-sm text-muted-foreground line-clamp-4">
          {review.comment_text}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <div className="flex items-center gap-1">
            {review.outlets && (
              <>
                <MapPin className="h-3 w-3" />
                <span>{review.outlets.outlet_name}</span>
              </>
            )}
          </div>
          {review.posted_at && (
            <span>{format(new Date(review.posted_at), "MMM d, yyyy")}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
